import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';

const AuthContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const AuthForm = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Title = styled.h1`
  margin-bottom: 10px;
  color: #333;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 30px;
`;

const OAuthButton = styled.button<{ provider: string }>`
  width: 100%;
  padding: 12px 20px;
  margin: 10px 0;
  border: 2px solid #ddd;
  border-radius: 8px;
  background: white;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.provider === 'google' ? '#4285f4' : '#333'};
    background: ${props => props.provider === 'google' ? '#f8f9fa' : '#f6f8fa'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GoogleIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3csvg width='18' height='18' xmlns='http://www.w3.org/2000/svg'%3e%3cg fill='none' fill-rule='evenodd'%3e%3cpath d='M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z' fill='%234285F4'/%3e%3cpath d='M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z' fill='%2334A853'/%3e%3cpath d='M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z' fill='%23FBBC05'/%3e%3cpath d='M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z' fill='%23EA4335'/%3e%3c/g%3e%3c/svg%3e");
  background-size: contain;
`;

const GitHubIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 10 4.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0 0 20 10.017C20 4.484 15.522 0 10 0Z' fill='%23181717'/%3e%3c/svg%3e");
  background-size: contain;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
`;

const Features = styled.div`
  margin-top: 30px;
  text-align: left;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  margin: 8px 0;
  color: #666;
  font-size: 14px;

  &:before {
    content: "✓";
    color: #4CAF50;
    font-weight: bold;
    margin-right: 10px;
  }
`;

export const AuthPage: React.FC = () => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { loginWithOAuth } = useAuth();

  const handleOAuthLogin = async (provider: 'google' | 'github') => {
    setIsLoading(true);
    setError('');

    try {
      await loginWithOAuth(provider);
    } catch (error: any) {
      setError(error.response?.data?.message || `${provider} 登录失败，请重试`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AuthForm>
        <Title>全球涂鸦占领</Title>
        <Subtitle>在世界地图上创建你的专属领土</Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <OAuthButton
          provider="google"
          onClick={() => handleOAuthLogin('google')}
          disabled={isLoading}
        >
          <GoogleIcon />
          使用 Google 账号登录
        </OAuthButton>

        <OAuthButton
          provider="github"
          onClick={() => handleOAuthLogin('github')}
          disabled={isLoading}
        >
          <GitHubIcon />
          使用 GitHub 账号登录
        </OAuthButton>

        <Features>
          <FeatureItem>创建个性化 32x32 涂鸦标识</FeatureItem>
          <FeatureItem>在全球地图上占领区域</FeatureItem>
          <FeatureItem>每日登录获得涂鸦点奖励</FeatureItem>
          <FeatureItem>与全球玩家竞争领土</FeatureItem>
          <FeatureItem>完全免费，无广告</FeatureItem>
        </Features>
      </AuthForm>
    </AuthContainer>
  );
};